import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { fetchBySlug } from '../lib/cms';
import useDocumentMeta from '../hooks/useDocumentMeta';
import { CheckCircle2, Heart, Shield, Sparkles, HelpCircle } from 'lucide-react';

export default function Maalgruppe() {
  useDocumentMeta(
    'Målgruppe | LIVSLYST ApS',
    'LIVSLYST ApS henvender sig til voksne borgere under folkepensionsalderen med nedsat fysisk eller psykisk funktionsevne, som opfylder betingelserne for §103.'
  );

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPage() {
      const data = await fetchBySlug('da', 'maalgruppe');
      setPageData(data);
      setLoading(false);
    }
    loadPage();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh] bg-stone-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  const title = pageData?.title?.da || pageData?.title || 'Målgruppe';
  const intro = pageData?.intro?.da || pageData?.intro || '';
  const content = pageData?.content?.da || pageData?.content || '';
  const image = pageData?.image?.[0] || 'https://assets.auxodev.dk/assets/8eff581d-09aa-4f3c-b275-323f2d379ce9/20961382-daba-40ee-b4d1-ace995af160f.jpeg';

  const targetPoints = [
    'Borgere med nedsat fysisk eller psykisk funktionsevne under folkepensionsalderen.',
    'Personer med kognitive udfordringer eller udviklingsforstyrrelser (f.eks. inden for autismespektret).',
    'Borgere med erhvervet hjerneskade, som har brug for særligt tilpassede rammer.',
    'Personer med særlige sociale udfordringer, der gør det svært at fastholde beskæftigelse på normale vilkår.',
    'Borgere, der er bevilget beskyttet beskæftigelse efter servicelovens §103 af deres bopælskommune.',
  ];

  const focusAreas = [
    {
      title: 'Individuelle behov',
      description: 'Hver enkelt borger mødes med udgangspunkt i deres unikke ressourcer, interesser og dagsform.',
      icon: Heart,
    },
    {
      title: 'Tryghed & støtte',
      description: 'Et nærværende og højtuddannet pædagogisk personale sikrer tryghed og støtte i alle situationer.',
      icon: Shield,
    },
    {
      title: 'Udvikling & succes',
      description: 'Gennem tilpassede arbejdsopgaver og succesoplevelser styrker vi borgerens selvværd og kompetencer.',
      icon: Sparkles,
    },
  ];

  return (
    <div className="bg-stone-50 text-stone-800 antialiased overflow-x-hidden">
      {/* Header Section */}
      <section className="bg-stone-100 py-16 md:py-20 border-b border-stone-200">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
            Hvem henvender vi os til?
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 tracking-tight">
            {title}
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            {intro ? (
              (() => {
                const match = intro.match(/(LIVSLYST(?:\s+ApS)?|Livslyst(?:\s+ApS)?)/i);
                if (!match) return intro;
                const index = match.index;
                const matchedText = match[0];
                return (
                  <>
                    {intro.slice(0, index)}
                    <Link to="/" className="font-semibold text-emerald-800 hover:text-emerald-900 transition-colors">
                      {matchedText}
                    </Link>
                    {intro.slice(index + matchedText.length)}
                  </>
                );
              })()
            ) : (
              <>
                <Link to="/" className="font-semibold text-emerald-800 hover:text-emerald-900 transition-colors">LIVSLYST ApS</Link> henvender sig til voksne borgere under folkepensionsalderen, som opfylder betingelserne for §103.
              </>
            )}
          </p>
        </motion.div>
      </section>

      {/* Main Description Section */}
      <section className="py-16 md:py-24 bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Image Column */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="lg:col-span-5 order-last lg:order-first"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-emerald-800 rounded-2xl -rotate-2 scale-95 opacity-5" />
                <img
                  src={image}
                  alt="Målgruppe hos LIVSLYST"
                  className="relative z-10 w-full h-[400px] object-cover rounded-2xl shadow-md border border-stone-200"
                />
              </div>
            </motion.div>

            {/* Text Column */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="lg:col-span-7 space-y-6"
            >
              <h2 className="text-3xl font-bold text-stone-900 tracking-tight">
                Målgruppebeskrivelse Livslyst - STU
              </h2>
              <div 
                className="text-stone-600 leading-relaxed space-y-4 text-base"
                dangerouslySetInnerHTML={{ __html: content }}
              />
              <div className="flex flex-wrap gap-4 pt-4">
                <a 
                  href="/stu"
                  className="inline-flex items-center justify-center px-6 py-3 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold shadow-md transition-colors text-sm"
                >
                  Læs mere om STU
                </a>
                <a 
                  href="/kontakt"
                  className="inline-flex items-center justify-center px-6 py-3 border-2 border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white rounded-lg font-semibold shadow-sm hover:shadow-md transition-all text-sm"
                >
                  Kontakt os
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Target Points List */}
      <section className="py-16 md:py-24 bg-stone-50 border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
              Kriterier for visitation
            </span>
            <h2 className="text-3xl font-bold text-stone-900 tracking-tight">
              Hvem opfylder betingelserne?
            </h2>
            <p className="text-stone-600 text-base leading-relaxed">
              Vores tilbud er tilpasset borgere, som har behov for beskyttede rammer og pædagogisk støtte i hverdagen.
            </p>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="bg-white p-8 md:p-10 rounded-2xl border border-stone-200 shadow-sm space-y-5"
          >
            {targetPoints.map((point, idx) => (
              <div key={idx} className="flex items-start gap-3.5">
                <CheckCircle2 className="w-5.5 h-5.5 text-emerald-600 shrink-0 mt-0.5" />
                <p className="text-stone-700 text-base leading-relaxed">{point}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Focus Areas Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
              Vores pædagogiske fokus
            </span>
            <h2 className="text-3xl font-bold text-stone-900 tracking-tight">
              Støtte der gør en forskel i hverdagen
            </h2>
            <p className="text-stone-600 text-base leading-relaxed">
              Vi arbejder helhedsorienteret for at sikre, at den enkelte borger trives og oplever hverdagsstruktur og personlig udvikling.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {focusAreas.map((area, idx) => {
              const Icon = area.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-stone-50 p-8 rounded-2xl border border-stone-200 flex flex-col items-start space-y-4"
                >
                  <div className="p-3 bg-emerald-50 rounded-xl text-emerald-800">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold text-stone-900">{area.title}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{area.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
